/**
 * Canonical Metrics - Single Source of Truth
 *
 * This module provides THE canonical source for all Bitcoin metrics.
 * Both the Dashboard (DataBanner) and AI Service consume this.
 *
 * IMPORTANT: Any metric calculation changes should be made HERE,
 * not in DataBanner or aiService.
 */

import { getFallbackValue } from '../fallbackCalculations';
import { formatNumber, formatLargeNumber } from '../number.utils';
import { daysSince, daysLeft } from '../date.utils';
import { currencySymbols } from '../currencyConversion.utils';
import { isS08DataValid, mergeWithFallbackSync } from './blockchainStatsFallback';

// ============================================================================
// FORMATTERS (shared between display and AI)
// ============================================================================

// Drop trailing zeros: 1.0 -> 1, 1.20 -> 1.2, 1.23 -> 1.23
export const clean = (n, decimals = 1) => {
  const s = n.toFixed(decimals);
  if (!s.includes('.')) return s;
  return s.replace(/\.?0+$/, '');
};

export const fmt = {
  price: (val) => val ? `$${parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A',
  pct: (val, decimals = 1) => val !== undefined && val !== null ? `${clean(parseFloat(val), decimals)}%` : 'N/A',
  billion: (val) => val ? `$${clean(val / 1e9, 2)}B` : 'N/A',
  hashrate: (val) => {
    if (!val) return 'N/A';
    const num = parseFloat(val);
    if (num >= 1e21) return `~${clean(num / 1e21)} ZH/s`;
    if (num >= 1e18) return `~${clean(num / 1e18)} EH/s`;
    return `~${clean(num / 1e15)} PH/s`;
  },
  difficulty: (val) => val ? `${clean(val / 1e12, 2)} T` : 'N/A',
  sats: (val) => val ? `${(val / 1e8).toFixed(8)} BTC` : 'N/A',
  btc: (val) => val ? `${(val / 1e8).toFixed(8)} BTC` : 'N/A',
  btcShort: (val) => {
    if (!val) return 'N/A';
    const btc = parseFloat(val) / 1e8;
    return btc.toLocaleString(undefined, { maximumFractionDigits: 1 }).replace(/\.0$/, '');
  },
  bytes: (val) => val ? `${clean(val / 1e6, 2)} MB` : 'N/A',
  num: (val) => val ? val.toLocaleString() : 'N/A',
  thousands: (val) => val ? `${clean(val / 1000)}k` : 'N/A',
  time: (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${clean(minutes)} min`;
    return `${clean(minutes / 60)} hrs`;
  }
};

// Helper to format without trailing zeros
const formatNumberClean = (value, decimals = 0) => {
  const formatted = formatNumber(value, decimals);
  if (decimals > 0 && formatted.includes('.')) {
    return formatted.replace(/\.?0+$/, '');
  }
  return formatted;
};

// ============================================================================
// HALVING CALCULATIONS (single source of truth)
// ============================================================================

const HALVING_INTERVAL = 210000;
const INITIAL_SUBSIDY = 50;

// Derive circulating supply from block height using the halving schedule
export const calculateCirculatingFromHeight = (blockHeight) => {
  if (!blockHeight || blockHeight < 0) return null;
  let totalMined = 0;
  let subsidy = INITIAL_SUBSIDY;
  let remaining = blockHeight;

  while (remaining > 0) {
    const blocksInEpoch = Math.min(remaining, HALVING_INTERVAL);
    totalMined += blocksInEpoch * subsidy;
    remaining -= blocksInEpoch;
    subsidy /= 2;
  }

  return totalMined;
};

export const calculateHalvingProgress = (blockHeight) => {
  if (!blockHeight) return null;
  const halvingCount = Math.floor(blockHeight / HALVING_INTERVAL);
  const startHeight = halvingCount * HALVING_INTERVAL;
  const endHeight = (halvingCount + 1) * HALVING_INTERVAL;
  const progress = ((blockHeight - startHeight) / (endHeight - startHeight)) * 100;
  const blocksLeft = endHeight - blockHeight;
  const estimatedDays = Math.round((blocksLeft * 10) / 60 / 24);

  return {
    progress: Math.round(progress * 10) / 10,
    blocksLeft,
    estimatedDays,
    currentEpoch: halvingCount + 1,
    nextHalvingHeight: endHeight
  };
};

// ============================================================================
// DIFFICULTY ADJUSTMENT CALCULATIONS
// ============================================================================

export const calculateDifficultyProgress = (da) => {
  if (!da?.progressPercent) return null;
  return {
    progress: Math.round(da.progressPercent * 10) / 10,
    blocksLeft: da.remainingBlocks,
    estimatedChange: da.difficultyChange != null
      ? `${da.difficultyChange >= 0 ? '+' : ''}${da.difficultyChange.toFixed(2)}%`
      : null,
    previousRetarget: da.previousRetarget,
    estimatedRetargetDate: da.estimatedRetargetDate,
    remainingTime: da.remainingTime
  };
};

// ============================================================================
// SAFE PERCENT CHANGE HELPER
// ============================================================================

const safePercentChange = (current, previous) => {
  if (!current || !previous || previous === 0) return null;
  const change = ((current - previous) / previous * 100);
  return isFinite(change) ? change : null;
};

// ============================================================================
// MAIN CANONICAL METRICS FUNCTION
// ============================================================================

/**
 * Calculate all canonical metrics from raw data sources
 *
 * @param {Object} sources - All data sources
 * @param {Object} sources.data - API data from dataOrchestrator
 * @param {number|string} sources.bitcoinPrice - Current BTC price (WebSocket priority)
 * @param {Object} sources.lastMempoolBlockData - Latest block from Mempool.space
 * @param {Array} sources.recentBlocks - Recent blocks array
 * @param {Object} sources.fees - Fee recommendations
 * @param {Object} sources.mempoolInfo - Mempool statistics
 * @param {Object} sources.da - Difficulty adjustment data
 * @param {Object} sources.diamondbackData - Diamondback API stats
 * @param {Object} sources.blockMetrics - Computed block metrics
 * @param {string} sources.selectedCurrency - User's selected currency (default: 'usd')
 * @param {Object} sources.conversionRates - Currency conversion rates
 * @returns {Object} Canonical metrics object
 */
export const calculateCanonicalMetrics = (sources) => {
  const {
    data = {},
    bitcoinPrice,
    lastMempoolBlockData,
    recentBlocks = [],
    fees = {},
    mempoolInfo = {},
    da = {},
    diamondbackData: _diamondbackData = {}, // Reserved for future use
    blockMetrics = {},
    selectedCurrency = 'usd',
    conversionRates = {}
  } = sources;
  void _diamondbackData; // Suppress unused variable warning

  // Parse bitcoin price
  const btcPrice = parseFloat(bitcoinPrice) || null;

  // Currency conversion helper
  const convertPrice = (usdValue) => {
    if (!usdValue || !conversionRates[selectedCurrency]) return usdValue;
    return usdValue * conversionRates[selectedCurrency];
  };

  // Current block height
  const blockHeight = lastMempoolBlockData?.height ||
    data?.s03?.bitcoin_stats?.bitcoin_data?.blocks ||
    null;

  // Halving data
  const halving = calculateHalvingProgress(blockHeight);

  // Difficulty adjustment data
  const difficultyAdj = calculateDifficultyProgress(da);

  // === RAW VALUES ===
  // These are the canonical raw values - use these for calculations

  const raw = {
    // Price & Market
    price: btcPrice,
    satsPerDollar: btcPrice ? Math.round(100000000 / btcPrice) : null,
    priceChange1h: data?.s06?.bitcoin?.market_data?.price_change_percentage_1h_in_currency?.[selectedCurrency],
    priceChange24h: data?.s06?.bitcoin?.market_data?.price_change_percentage_24h_in_currency?.[selectedCurrency],
    priceChange7d: data?.s06?.bitcoin?.market_data?.price_change_percentage_7d,
    priceChange14d: data?.s06?.bitcoin?.market_data?.price_change_percentage_14d,
    priceChange30d: data?.s06?.bitcoin?.market_data?.price_change_percentage_30d,
    priceChange60d: data?.s06?.bitcoin?.market_data?.price_change_percentage_60d,
    priceChange200d: data?.s06?.bitcoin?.market_data?.price_change_percentage_200d_in_currency?.[selectedCurrency],
    priceChange1y: data?.s06?.bitcoin?.market_data?.price_change_percentage_1y,
    priceChangeMTD: data?.s12?.metrics?.data?.[0]?.returnOnInvestment?.priceChangeMTD,
    priceChangeYTD: data?.s12?.metrics?.data?.[0]?.returnOnInvestment?.priceChangeYTD,
    high24h: data?.s06?.bitcoin?.market_data?.high_24h?.[selectedCurrency],
    low24h: data?.s06?.bitcoin?.market_data?.low_24h?.[selectedCurrency],
    cycleLow: data?.s12?.metrics?.data?.[0]?.allTimeHigh?.cycleLow,
    cycleLowPercentUp: data?.s12?.metrics?.data?.[0]?.allTimeHigh?.cycleLowPercentUp,
    breakevenMultiple: data?.s12?.metrics?.data?.[0]?.allTimeHigh?.breakevenMultiple,
    // ATH calculation - same logic as MarketTileAthPercentage
    ath: (() => {
      const apiAth = data?.s06?.bitcoin?.market_data?.ath?.usd;
      return (apiAth != null && btcPrice != null) ? Math.max(apiAth, btcPrice) : apiAth;
    })(),
    athChangePercent: (() => {
      // Calculate ATH% exactly like MarketTileAthPercentage - use live btcPrice
      const apiAth = data?.s06?.bitcoin?.market_data?.ath?.usd;
      const actualAth = apiAth != null && btcPrice != null
        ? Math.max(apiAth, btcPrice)
        : apiAth ?? btcPrice;
      return actualAth != null && btcPrice != null
        ? ((btcPrice - actualAth) / actualAth) * 100
        : null; // Don't fall back to stale API value
    })(),
    marketCap: data?.s06?.bitcoin?.market_data?.market_cap?.usd,
    marketCapChangePercent24h: data?.s06?.bitcoin?.market_data?.market_cap_change_percentage_24h,
    volume24h: data?.s06?.bitcoin?.market_data?.total_volume?.usd,

    // Network
    hashrate: data?.ts04?.hashrate?.currentHashrate,
    hashrate30dAvg: data?.s12?.metrics?.data?.mining_stats?.hash_rate_30d_average,
    difficulty: lastMempoolBlockData?.difficulty || data?.s03?.bitcoin_stats?.bitcoin_data?.difficulty,
    nextDifficultyEstimate: data?.s03?.bitcoin_stats?.bitcoin_data?.next_difficulty_estimate,
    blockHeight,
    nodes: data?.s01?.latest_snapshot?.total_nodes || data?.s03?.bitcoin_stats?.bitcoin_data?.nodes,
    blockchainSize: data?.s03?.bitcoin_stats?.bitcoin_data?.blockchain_size,

    // Mining pool distribution (top 5 from latest ts05 snapshot)
    miningPoolDistribution: (() => {
      const pools = data?.ts05?.hashrate_pools;
      if (!pools?.length) return null;
      const maxTs = Math.max(...pools.map(p => p.timestamp));
      return pools
        .filter(p => p.timestamp === maxTs)
        .sort((a, b) => b.share - a.share)
        .slice(0, 5)
        .map(p => ({ name: p.poolName, share: p.share }));
    })(),

    // Block metrics (from blockMetrics or fallback)
    avgBlockTime: blockMetrics?.avgBlockTime || getFallbackValue('avgBlockTime', { recentBlocks, s091: data?.s091 }),
    // Prefer API value for blocks24h (more reliable than WebSocket-calculated)
    blocks24h: data?.s12?.metrics?.data?.blockchain_stats_24_hours?.count_of_blocks_added
      || (blockMetrics?.blocks24h > 50 ? blockMetrics.blocks24h : null)
      || getFallbackValue('newBlocks24h', { recentBlocks, s091: data?.s091 }),

    // Mining
    miningRevenue24h: data?.s12?.metrics?.data?.mining_stats?.mining_revenue_usd,
    miningRevenueFeesPercent: data?.s12?.metrics?.data?.mining_stats?.mining_revenue_from_fees_percent_last_24_hours,
    blockReward: lastMempoolBlockData?.extras?.reward, // in satoshis
    blockSubsidy: data?.s03?.halvening?.bitcoin_data?.current_reward || 312500000, // 3.125 BTC in sats

    // Last block
    lastBlock: lastMempoolBlockData,
    lastBlockHeight: lastMempoolBlockData?.height,
    lastBlockTxCount: lastMempoolBlockData?.tx_count,
    lastBlockSize: lastMempoolBlockData?.size,
    lastBlockWeight: lastMempoolBlockData?.weight,
    lastBlockTotalFees: lastMempoolBlockData?.extras?.totalFees, // in satoshis
    lastBlockMedianFee: lastMempoolBlockData?.extras?.medianFee,
    lastBlockAvgFeeRate: lastMempoolBlockData?.extras?.avgFeeRate,
    lastBlockPool: lastMempoolBlockData?.extras?.pool?.name,
    lastBlockUtxoChange: lastMempoolBlockData?.extras?.utxoSetChange,
    lastBlockFeeRange: lastMempoolBlockData?.extras?.feeRange,
    lastBlockSegwitTxs: lastMempoolBlockData?.extras?.segwitTotalTxs,
    lastBlockSegwitWeight: lastMempoolBlockData?.extras?.segwitTotalWeight,
    lastBlockMatchRate: lastMempoolBlockData?.extras?.matchRate,
    lastBlockTotalInputs: lastMempoolBlockData?.extras?.totalInputs,
    lastBlockTotalOutputs: lastMempoolBlockData?.extras?.totalOutputs,

    // Fee market
    fastestFee: fees?.fastestFee,
    halfHourFee: fees?.halfHourFee,
    hourFee: fees?.hourFee,
    economyFee: fees?.economyFee,
    minimumFee: fees?.minimumFee,

    // Mempool
    mempoolSize: mempoolInfo?.size, // tx count
    mempoolUsage: mempoolInfo?.usage, // bytes
    mempoolMaxSize: mempoolInfo?.maxmempool,
    mempoolTotalFee: mempoolInfo?.total_fee, // in BTC

    // Difficulty adjustment
    daProgress: da?.progressPercent,
    daEstimatedChange: da?.difficultyChange,
    daRemainingBlocks: da?.remainingBlocks,
    daPreviousRetarget: da?.previousRetarget,
    daEstimatedRetargetDate: da?.estimatedRetargetDate,
    daRemainingTime: da?.remainingTime,

    // Halving
    halvingProgress: halving?.progress,
    halvingBlocksLeft: halving?.blocksLeft,
    halvingEstimatedDays: halving?.estimatedDays,
    halvingCurrentEpoch: halving?.currentEpoch,

    // Supply (derived from block height — deterministic, no API dependency)
    circulatingSupply: calculateCirculatingFromHeight(blockHeight) || data?.s12?.metrics?.data?.[0]?.marketData?.supply?.circulating,
    leftToMine: blockHeight ? 21000000 - calculateCirculatingFromHeight(blockHeight) : null,

    // Lightning
    lnNodes: data?.s11?.lightning?.latest?.node_count,
    lnChannels: data?.s11?.lightning?.latest?.channel_count,
    lnCapacity: data?.s11?.lightning?.latest?.total_capacity, // in satoshis
    lnAvgCapacity: data?.s11?.lightning?.latest?.avg_capacity,
    lnAvgFeeRate: data?.s11?.lightning?.latest?.avg_fee_rate,
    lnNodeChange: safePercentChange(
      data?.s11?.lightning?.latest?.node_count,
      data?.s11?.lightning?.previous?.node_count
    ),
    lnChannelChange: safePercentChange(
      data?.s11?.lightning?.latest?.channel_count,
      data?.s11?.lightning?.previous?.channel_count
    ),
    lnCapacityChange: safePercentChange(
      data?.s11?.lightning?.latest?.total_capacity,
      data?.s11?.lightning?.previous?.total_capacity
    ),
    lnTorNodes: data?.s11?.lightning?.latest?.tor_nodes,
    lnClearnetNodes: data?.s11?.lightning?.latest?.clearnet_nodes,
    lnClearnetTorNodes: data?.s11?.lightning?.latest?.clearnet_tor_nodes,
    lnUnannouncedNodes: data?.s11?.lightning?.latest?.unannounced_nodes,
    lnMedianCapacity: data?.s11?.lightning?.latest?.med_capacity,
    lnMedianFeeRate: data?.s11?.lightning?.latest?.med_fee_rate,

    // On-chain activity (with fallback for rate-limited s08)
    transactions24h: (() => {
      const s08Valid = isS08DataValid(data?.s08 ? { blockchain: data.s08.blockchain } : null);
      if (s08Valid) return data?.s08?.blockchain?.Data?.transaction_count;
      const fallback = mergeWithFallbackSync(data?.s08 ? { blockchain: data.s08.blockchain } : null);
      return fallback.transactions24h;
    })(),
    activeAddresses24h: (() => {
      const s08Valid = isS08DataValid(data?.s08 ? { blockchain: data.s08.blockchain } : null);
      if (s08Valid) return data?.s08?.blockchain?.Data?.active_addresses;
      const fallback = mergeWithFallbackSync(data?.s08 ? { blockchain: data.s08.blockchain } : null);
      return fallback.activeAddresses || fallback.hodlingAddresses;
    })(),
    newAddresses24h: (() => {
      const s08Valid = isS08DataValid(data?.s08 ? { blockchain: data.s08.blockchain } : null);
      return s08Valid ? data?.s08?.blockchain?.Data?.new_addresses : null;
    })(),
    avgTxValue: (() => {
      const s08Valid = isS08DataValid(data?.s08 ? { blockchain: data.s08.blockchain } : null);
      return s08Valid ? data?.s08?.blockchain?.Data?.average_transaction_value : null;
    })(),
    largeTxCount: (() => {
      const s08Valid = isS08DataValid(data?.s08 ? { blockchain: data.s08.blockchain } : null);
      return s08Valid ? data?.s08?.blockchain?.Data?.large_transaction_count : null;
    })(),
    totalTransactions: data?.s03?.bitcoin_stats?.bitcoin_data?.transactions,
    hodlingAddresses: data?.s03?.bitcoin_stats?.bitcoin_data?.hodling_addresses,
    mempoolTps: data?.s03?.bitcoin_stats?.bitcoin_data?.mempool_tps,
    transactionVolume24h: data?.s03?.bitcoin_stats?.bitcoin_data?.volume_24h,
    inflationBtc24h: data?.s03?.bitcoin_stats?.bitcoin_data?.inflation_24h,
    inflationUsd24h: data?.s03?.bitcoin_stats?.bitcoin_data?.inflation_usd_24h,
    medianTxFeeSats24h: data?.s03?.bitcoin_stats?.bitcoin_data?.median_transaction_fee_24h,
    avgTxFeeUsd24h: data?.s03?.bitcoin_stats?.bitcoin_data?.average_transaction_fee_usd_24h,
    cdd24h: data?.s03?.bitcoin_stats?.bitcoin_data?.cdd_24h,
    largestTx24h: data?.s03?.bitcoin_stats?.bitcoin_data?.largest_transaction_24h?.value_usd,
    utxoCount: data?.s12?.metrics?.data?.on_chain_data?.utxo_count_last_24_hours,
    avgUtxoAge: data?.s12?.metrics?.data?.on_chain_data?.average_utxo_age,

    // Rewards (from ts11)
    rewards144: data?.ts11?.rewards144, // Last 144 blocks rewards
    rewards1: data?.ts11?.rewards1,     // Last block rewards

    // Asset info
    assetAge: daysSince("2009-01-03"),

    // Commodities (for "One BTC Can Buy")
    goldPriceXau: data?.s06?.bitcoin?.market_data?.current_price?.xag,
    silverPriceXag: data?.s06?.bitcoin?.market_data?.current_price?.xag,
    oilPrice: data?.s15?.oil_usd?.[0]?.spreadProfilePrices?.[0]?.ask,
    naturalGasPrice: data?.s15?.ngc_usd?.[0]?.spreadProfilePrices?.[0]?.ask,
  };

  // === FORMATTED VALUES ===
  // These are ready-to-display formatted strings

  const formatted = {
    // Price & Market
    bitcoinPrice: btcPrice && selectedCurrency && currencySymbols[selectedCurrency]
      ? (() => {
        const converted = convertPrice(btcPrice);
        const noDecimals = ['ars', 'jpy', 'krw', 'inr', 'php'].includes(selectedCurrency.toLowerCase());
        return `${currencySymbols[selectedCurrency]}${formatNumber(converted, noDecimals ? 0 : 2)}`;
      })()
      : 'Loading...',

    satsPerDollar: btcPrice
      ? formatNumber((1 / convertPrice(btcPrice)) * 100000000, 0)
      : 'Loading...',

    priceChange24h: raw.priceChange24h != null
      ? `${formatNumberClean(raw.priceChange24h, 2)}%`
      : 'Loading...',

    priceChange7d: raw.priceChange7d != null
      ? `${formatNumberClean(raw.priceChange7d, 2)}%`
      : 'Loading...',

    fromAth: raw.athChangePercent != null
      ? `${formatNumberClean(raw.athChangePercent, 2)}%`
      : 'Loading...',

    marketCap: raw.marketCap != null
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(convertPrice(raw.marketCap), 2, false)}`
      : 'Loading...',

    volume24h: raw.volume24h != null
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(convertPrice(raw.volume24h), 2, false)}`
      : 'Loading...',

    // Network
    blockHeight: blockHeight ? formatNumber(blockHeight, 0) : 'Loading...',

    hashRate: raw.hashrate != null
      ? `~${formatLargeNumber(raw.hashrate, 1, true)}H/s`
      : 'Loading...',

    hashRateFull: raw.hashrate != null
      ? `${formatLargeNumber(raw.hashrate, 1, true)}H/s`
      : 'Loading...',

    difficulty: raw.difficulty != null
      ? formatLargeNumber(raw.difficulty, 2, false)
      : 'Loading...',

    nextDifficultyEstimate: raw.nextDifficultyEstimate != null
      ? formatLargeNumber(raw.nextDifficultyEstimate, 1, true)
      : 'Loading...',

    nodes: raw.nodes != null ? `${formatNumber(raw.nodes, 0)} Nodes` : 'Loading...',

    // Block time
    avgBlockTime: raw.avgBlockTime != null
      ? `${formatNumberClean(raw.avgBlockTime / 60, 1)} Mins`
      : 'Loading...',

    newBlocks24h: raw.blocks24h != null
      ? formatNumberClean(raw.blocks24h, 0)
      : 'Loading...',

    // Mining
    miningRevenue24h: raw.miningRevenue24h != null && btcPrice
      ? `~${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(convertPrice(raw.miningRevenue24h), 1, false)}`
      : (data?.ts11?.rewards144?.totalReward != null && btcPrice
        ? `~${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(
          (Number(data.ts11.rewards144.totalReward) / 100000000) * convertPrice(btcPrice), 1, false
        )}`
        : 'Loading...'),

    blockRewardValue: raw.blockReward != null && btcPrice
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(
        (raw.blockReward / 100000000) * convertPrice(btcPrice), 0
      )}`
      : 'Loading...',

    // Fee market
    fastestFee: raw.fastestFee != null
      ? `${raw.fastestFee < 1 ? formatNumber(raw.fastestFee, 2) : formatNumber(raw.fastestFee, 0)} ṩ/vB`
      : 'Loading...',

    halfHourFee: raw.halfHourFee != null
      ? `${raw.halfHourFee < 1 ? formatNumber(raw.halfHourFee, 2) : formatNumber(raw.halfHourFee, 0)} ṩ/vB`
      : 'Loading...',

    hourFee: raw.hourFee != null
      ? `${raw.hourFee < 1 ? formatNumber(raw.hourFee, 2) : formatNumber(raw.hourFee, 0)} ṩ/vB`
      : 'Loading...',

    economyFee: raw.economyFee != null
      ? `${raw.economyFee < 1 ? formatNumber(raw.economyFee, 2) : formatNumber(raw.economyFee, 0)} ṩ/vB`
      : 'Loading...',

    // Mempool
    unconfirmedTxs: raw.mempoolSize != null
      ? (raw.mempoolSize < 10000
        ? `${formatNumber(raw.mempoolSize, 0)} TX`
        : `~${formatLargeNumber(raw.mempoolSize, 0, false)} TX`)
      : 'Loading...',

    mempoolSize: raw.mempoolUsage != null
      ? `${formatLargeNumber(raw.mempoolUsage, 1, true)}B`
      : 'Loading...',

    mempoolTotalFees: raw.mempoolTotalFee != null
      ? (() => {
        if (raw.mempoolTotalFee >= 1) {
          return `${formatNumberClean(raw.mempoolTotalFee, 8)} BTC`;
        } else {
          return `${formatLargeNumber(raw.mempoolTotalFee * 100000000, 1, false)} sats`;
        }
      })()
      : 'Loading...',

    mempoolValue: raw.mempoolTotalFee != null && btcPrice
      ? `${currencySymbols[selectedCurrency]}${formatNumber(raw.mempoolTotalFee * convertPrice(btcPrice), 0)}`
      : 'Loading...',

    // Difficulty adjustment
    diffChangePercent: da?.difficultyChange != null
      ? `${formatNumberClean(da.difficultyChange, 2)}%`
      : 'Loading...',

    diffProgressPercent: da?.progressPercent != null
      ? `${formatNumberClean(da.progressPercent, 2)}%`
      : 'Loading...',

    blocksToDiffAdj: da?.remainingBlocks != null
      ? `${da.remainingBlocks}`
      : 'Loading...',

    timeToNextDiff: da?.remainingTime != null
      ? `~${formatNumberClean(da.remainingTime / (1000 * 60 * 60 * 24), 1)} D`
      : 'Loading...',

    previousRetarget: da?.previousRetarget != null
      ? `${da.previousRetarget > 0 ? '+' : ''}${formatNumberClean(da.previousRetarget, 2)}%`
      : 'Loading...',

    estimatedRetargetDate: da?.estimatedRetargetDate != null
      ? (() => {
        const date = new Date(da.estimatedRetargetDate);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      })()
      : 'Loading...',

    // Halving
    blocksToHalving: halving?.blocksLeft != null
      ? formatNumber(halving.blocksLeft)
      : 'Loading...',

    daysToHalving: halving?.estimatedDays != null
      ? `~${halving.estimatedDays} Days`
      : (data?.s03?.halvening?.bitcoin_data?.halvening_time != null
        ? `~${formatNumber(daysLeft(data.s03.halvening.bitcoin_data.halvening_time), 0)} Days`
        : 'Loading...'),

    // Supply
    circulatingSupply: raw.circulatingSupply != null
      ? `~${formatLargeNumber(raw.circulatingSupply, 2, false)}`
      : 'Loading...',

    leftToMine: raw.leftToMine != null
      ? formatNumber(raw.leftToMine, 0)
      : 'Loading...',

    percentageIssued: raw.circulatingSupply != null
      ? `${formatNumberClean((raw.circulatingSupply / 21000000) * 100, 2)}%`
      : 'Loading...',

    // Lightning
    lightningNodes: raw.lnNodes != null
      ? formatNumber(raw.lnNodes, 0)
      : 'Loading...',

    lightningChannels: raw.lnChannels != null
      ? formatNumber(raw.lnChannels, 0)
      : 'Loading...',

    lightningCapacity: raw.lnCapacity != null
      ? `${formatNumber(raw.lnCapacity / 100000000, 0)} BTC`
      : 'Loading...',

    lightningAvgChannelSize: raw.lnCapacity != null && raw.lnChannels != null
      ? formatNumberClean((raw.lnCapacity / raw.lnChannels) / 100000000, 6)
      : 'Loading...',

    // On-chain
    transactions24h: raw.transactions24h != null
      ? formatNumber(raw.transactions24h, 0)
      : 'Loading...',

    totalTransactions: raw.totalTransactions != null
      ? formatLargeNumber(raw.totalTransactions, 2, false)
      : 'Loading...',

    avgTxValue: raw.avgTxValue != null
      ? `${formatNumberClean(raw.avgTxValue, 3)} BTC`
      : 'Loading...',

    cdd24h: raw.cdd24h != null
      ? formatLargeNumber(raw.cdd24h, 1, true)
      : 'Loading...',

    largestTx24h: raw.largestTx24h != null && conversionRates[selectedCurrency]
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatLargeNumber(raw.largestTx24h * conversionRates[selectedCurrency], 2, false)}`
      : 'Loading...',

    // Last block
    lastBlockTxCount: raw.lastBlockTxCount != null
      ? `${formatNumber(raw.lastBlockTxCount, 0)} TX`
      : 'Loading...',

    lastBlockSize: raw.lastBlockSize != null
      ? `${formatLargeNumber(raw.lastBlockSize, 2, true)}B`
      : 'Loading...',

    lastBlockWeight: raw.lastBlockWeight != null
      ? `${formatLargeNumber(raw.lastBlockWeight, 2, true)} WU`
      : 'Loading...',

    lastBlockFees: raw.lastBlockTotalFees != null && btcPrice
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(
        (raw.lastBlockTotalFees / 100000000) * convertPrice(btcPrice), 0
      )}`
      : 'Loading...',

    lastBlockAvgFee: raw.lastBlockAvgFeeRate != null
      ? `${raw.lastBlockAvgFeeRate < 1 ? formatNumber(raw.lastBlockAvgFeeRate, 2) : formatNumber(raw.lastBlockAvgFeeRate, 0)} ṩ/vB`
      : 'Loading...',

    lastBlockMedianFee: raw.lastBlockMedianFee != null
      ? `${raw.lastBlockMedianFee < 1 ? formatNumber(raw.lastBlockMedianFee, 2) : formatNumber(raw.lastBlockMedianFee, 0)} ṩ/vB`
      : 'Loading...',

    lastBlockPool: raw.lastBlockPool || 'Loading...',

    lastBlockUtxoChange: raw.lastBlockUtxoChange != null
      ? `${raw.lastBlockUtxoChange > 0 ? '+' : ''}${formatNumber(raw.lastBlockUtxoChange, 0)}`
      : 'Loading...',

    blockFullness: raw.lastBlockWeight != null
      ? `${formatNumberClean((raw.lastBlockWeight / 4000000) * 100, 1)}%`
      : 'Loading...',

    availableBlockSpace: raw.lastBlockWeight != null
      ? `${formatLargeNumber(4000000 - raw.lastBlockWeight, 2, true)} WU`
      : 'Loading...',

    feesToSubsidyPercent: raw.lastBlockTotalFees != null && raw.blockReward != null
      ? `${formatNumberClean((raw.lastBlockTotalFees / raw.blockReward) * 100, 2)}%`
      : 'Loading...',

    // Asset age
    assetAge: formatNumber(raw.assetAge),

    // One BTC can buy
    goldToBtc: data?.s06?.bitcoin?.market_data?.current_price?.xau != null
      ? `${formatNumberClean(data.s06.bitcoin.market_data.current_price.xau, 1)} oz.`
      : 'Loading...',

    silverToBtc: data?.s06?.bitcoin?.market_data?.current_price?.xag != null
      ? `${formatLargeNumber(data.s06.bitcoin.market_data.current_price.xag, 1, false)} oz.`
      : 'Loading...',

    oilBarrelsToBtc: raw.oilPrice != null && btcPrice
      ? formatNumber(btcPrice / raw.oilPrice, 0)
      : 'Loading...',

    bigMacsToBtc: btcPrice ? formatNumber(btcPrice / 5.15, 0) : 'Loading...',
    coffeesToBtc: btcPrice ? formatNumber(btcPrice / 4.50, 0) : 'Loading...',
    housesToBtc: btcPrice ? formatNumberClean(btcPrice / 400000, 2) : 'Loading...',
    eggsToBtc: btcPrice ? formatNumber(btcPrice / 0.25, 0) : 'Loading...',
    iPhonesToBtc: btcPrice ? formatNumber(btcPrice / 999, 0) : 'Loading...',
    naturalGasToBtc: btcPrice && raw.naturalGasPrice
      ? formatNumberClean(btcPrice / raw.naturalGasPrice, 0)
      : 'Loading...',

    // Special: 6.15 BTC value
    sixOneFive: btcPrice
      ? `${selectedCurrency === 'usd' ? '$' : ''}${formatNumber(6.15 * convertPrice(btcPrice), 0)}`
      : 'Loading...',
  };

  // === COMPUTED PROGRESS DATA ===
  // Pre-calculated values for progress bars (use these instead of AI calculations)

  const progress = {
    halving: halving ? {
      progress: halving.progress,
      blocksLeft: halving.blocksLeft,
      estimatedDays: halving.estimatedDays,
      currentEpoch: halving.currentEpoch,
      detail: `${halving.blocksLeft.toLocaleString()} blocks (~${halving.estimatedDays} days)`
    } : null,

    difficulty: difficultyAdj ? {
      progress: difficultyAdj.progress,
      blocksLeft: difficultyAdj.blocksLeft,
      estimatedChange: difficultyAdj.estimatedChange,
      estimatedDays: difficultyAdj.remainingTime
        ? Math.round(difficultyAdj.remainingTime / (1000 * 60 * 60 * 24))
        : (difficultyAdj.blocksLeft ? Math.round((difficultyAdj.blocksLeft * 10) / 60 / 24) : null),
      detail: `${difficultyAdj.blocksLeft?.toLocaleString() || '?'} blocks, est ${difficultyAdj.estimatedChange || 'N/A'}`
    } : null
  };

  return {
    raw,
    formatted,
    progress,
    // Metadata
    meta: {
      selectedCurrency,
      timestamp: Date.now(),
      hasPrice: btcPrice != null,
      hasLastBlock: lastMempoolBlockData != null,
      hasFees: fees?.fastestFee != null,
      hasMempool: mempoolInfo?.size != null,
      hasDa: da?.progressPercent != null
    }
  };
};

// ============================================================================
// AI-SPECIFIC FORMATTERS
// ============================================================================

/**
 * Format metrics for AI consumption (text-based)
 * Returns a structured text block for AI analysis
 */
export const formatMetricsForAI = (metrics, selectedCategories = null) => {
  const { raw } = metrics;

  // Build sections
  const sections = {};

  sections['BITCOIN MARKET DATA'] = '\n' + [
    `Current Price: ${fmt.price(raw.price)}`,
    raw.satsPerDollar ? `Sats per Dollar: ${fmt.num(raw.satsPerDollar)}` : null,
    raw.priceChange1h != null ? `1h Change: ${fmt.pct(raw.priceChange1h)}` : null,
    `24h Change: ${fmt.pct(raw.priceChange24h)}`,
    `7d Change: ${fmt.pct(raw.priceChange7d)}`,
    raw.priceChange14d != null ? `14d Change: ${fmt.pct(raw.priceChange14d)}` : null,
    `30d Change: ${fmt.pct(raw.priceChange30d)}`,
    raw.priceChange60d != null ? `60d Change: ${fmt.pct(raw.priceChange60d)}` : null,
    `200d Change: ${fmt.pct(raw.priceChange200d)}`,
    `1y Change: ${fmt.pct(raw.priceChange1y)}`,
    raw.priceChangeMTD != null ? `MTD Change: ${fmt.pct(raw.priceChangeMTD)}` : null,
    raw.priceChangeYTD != null ? `YTD Change: ${fmt.pct(raw.priceChangeYTD)}` : null,
    `24h Range: ${fmt.price(raw.low24h)} - ${fmt.price(raw.high24h)}`,
    `All-Time High (ATH): ${fmt.price(raw.ath)}`,
    `Distance from ATH (NOT a recent change — this is cumulative from the all-time peak): ${fmt.pct(raw.athChangePercent)}`,
    raw.cycleLow != null ? `Cycle Low: ${fmt.price(raw.cycleLow)}` : null,
    raw.cycleLowPercentUp != null ? `% Above Cycle Low: ${fmt.pct(raw.cycleLowPercentUp)}` : null,
    raw.breakevenMultiple != null ? `Breakeven Multiple from ATH: ${raw.breakevenMultiple.toFixed(2)}x` : null,
    `Market Cap: ${fmt.billion(raw.marketCap)}`,
    `24h Volume: ${fmt.billion(raw.volume24h)}`,
  ].filter(Boolean).join('\n');

  sections['NETWORK SECURITY'] = '\n' + [
    `Hashrate: ${fmt.hashrate(raw.hashrate)}`,
    `Difficulty: ${fmt.difficulty(raw.difficulty)}`,
    `Full Nodes: ${fmt.num(raw.nodes)}`,
    `Block Height: ${fmt.num(raw.blockHeight)}`,
    raw.blockchainSize ? `Blockchain Size: ${(raw.blockchainSize / 1e9).toFixed(1)} GB` : null,
    `Average Block Time: ${fmt.time(raw.avgBlockTime ? raw.avgBlockTime / 60 : null)}`,
    `Blocks Mined (24h): ${fmt.num(raw.blocks24h)}`,
    (raw.lastBlockSegwitWeight != null && raw.lastBlockWeight)
      ? `SegWit Adoption (last block): ${((raw.lastBlockSegwitWeight / raw.lastBlockWeight) * 100).toFixed(1)}%`
      : null,
  ].filter(Boolean).join('\n');

  sections['DIFFICULTY ADJUSTMENT'] = `
Estimated Change: ${raw.daEstimatedChange != null ? `${raw.daEstimatedChange.toFixed(2)}%` : 'N/A'}
Previous Retarget: ${raw.daPreviousRetarget != null ? `${raw.daPreviousRetarget.toFixed(2)}%` : 'N/A'}`;

  sections['HALVING & MINING ECONOMICS'] = '\n' + [
    `Block Subsidy: ${raw.blockSubsidy ? `${(raw.blockSubsidy / 1e8).toFixed(4)} BTC` : '3.125 BTC'}`,
    `Annual Inflation: ~0.83% (lower than gold's ~1.5-2%)`,
    (raw.lastBlockTotalFees != null && raw.blockSubsidy)
      ? `Fee/Subsidy Ratio (last block): ${((raw.lastBlockTotalFees / raw.blockSubsidy) * 100).toFixed(2)}%`
      : null,
    raw.miningPoolDistribution?.length
      ? `Top Mining Pools: ${raw.miningPoolDistribution.map(p => `${p.name} (${(p.share * 100).toFixed(1)}%)`).join(', ')}`
      : null,
    raw.lastBlockFeeRange?.length
      ? `Last Block Fee Range: ${raw.lastBlockFeeRange.map(f => f.toFixed(1)).join(', ')} sat/vB (min to max percentiles)`
      : null,
    raw.lastBlockWeight
      ? `Block Fullness: ${((raw.lastBlockWeight / 4000000) * 100).toFixed(1)}%`
      : null,
    raw.lastBlockSegwitTxs != null
      ? `SegWit Transactions (last block): ${fmt.num(raw.lastBlockSegwitTxs)}`
      : null,
    raw.lastBlockUtxoChange != null
      ? `UTXO Set Change (last block): ${raw.lastBlockUtxoChange > 0 ? '+' : ''}${fmt.num(raw.lastBlockUtxoChange)}`
      : null,
  ].filter(Boolean).join('\n');

  sections['LATEST BLOCK'] = `
Height: ${fmt.num(raw.lastBlockHeight)}
Mined By: ${raw.lastBlockPool || 'Unknown'}
Transactions: ${fmt.num(raw.lastBlockTxCount)}
Size: ${fmt.bytes(raw.lastBlockSize)}
Total Fees: ${fmt.sats(raw.lastBlockTotalFees)}
Median Fee Rate: ${raw.lastBlockMedianFee || 'N/A'} sat/vB`;

  sections['FEE MARKET'] = `
Fastest Fee: ${raw.fastestFee || 'N/A'} sat/vB
Half Hour Fee: ${raw.halfHourFee || 'N/A'} sat/vB
Hour Fee: ${raw.hourFee || 'N/A'} sat/vB
Economy Fee: ${raw.economyFee || 'N/A'} sat/vB`;

  sections['LIGHTNING NETWORK'] = '\n' + [
    `Total Nodes: ${fmt.num(raw.lnNodes)}`,
    raw.lnNodeChange ? `Week-over-Week Node Change: ${fmt.pct(raw.lnNodeChange)}` : null,
    `Total Channels: ${fmt.num(raw.lnChannels)}`,
    raw.lnChannelChange ? `Week-over-Week Channel Change: ${fmt.pct(raw.lnChannelChange)}` : null,
    `Total Capacity: ${fmt.btc(raw.lnCapacity)}`,
    raw.lnCapacityChange ? `Week-over-Week Capacity Change: ${fmt.pct(raw.lnCapacityChange)}` : null,
    raw.lnTorNodes != null ? `Tor Nodes: ${fmt.num(raw.lnTorNodes)}` : null,
    raw.lnClearnetNodes != null ? `Clearnet Nodes: ${fmt.num(raw.lnClearnetNodes)}` : null,
    raw.lnClearnetTorNodes != null ? `Clearnet+Tor Nodes: ${fmt.num(raw.lnClearnetTorNodes)}` : null,
    raw.lnUnannouncedNodes != null ? `Unannounced Nodes: ${fmt.num(raw.lnUnannouncedNodes)}` : null,
    (raw.lnMedianCapacity != null && raw.lnAvgCapacity != null)
      ? `Median Channel Capacity: ${fmt.sats(raw.lnMedianCapacity)} (avg: ${fmt.sats(raw.lnAvgCapacity)})`
      : (raw.lnAvgCapacity != null ? `Average Channel Capacity: ${fmt.sats(raw.lnAvgCapacity)}` : null),
    (raw.lnMedianFeeRate != null && raw.lnAvgFeeRate != null)
      ? `Median Fee Rate: ${fmt.num(raw.lnMedianFeeRate)} ppm (avg: ${fmt.num(raw.lnAvgFeeRate)} ppm)`
      : (raw.lnAvgFeeRate != null ? `Average Fee Rate: ${fmt.num(raw.lnAvgFeeRate)} ppm` : null),
  ].filter(Boolean).join('\n');

  sections['ON-CHAIN ACTIVITY'] = '\n' + [
    `Transactions (24h): ${fmt.num(raw.transactions24h)}`,
    `Large Transactions (>$100k): ${fmt.num(raw.largeTxCount)}`,
    `New Addresses (24h): ${fmt.num(raw.newAddresses24h)}`,
    `Active Addresses (24h): ${fmt.num(raw.activeAddresses24h)}`,
    raw.hodlingAddresses != null ? `HODLing Addresses: ${fmt.num(raw.hodlingAddresses)}` : null,
    raw.mempoolTps != null ? `Mempool TPS: ${raw.mempoolTps.toFixed(2)}` : null,
    raw.transactionVolume24h != null ? `24h Transaction Volume: ${fmt.btc(raw.transactionVolume24h)}` : null,
    raw.avgTxFeeUsd24h != null ? `Avg Transaction Fee: $${raw.avgTxFeeUsd24h.toFixed(2)}` : null,
    raw.inflationBtc24h != null ? `Daily Inflation (new BTC): ${fmt.btc(raw.inflationBtc24h)}` : null,
    raw.inflationUsd24h != null ? `Daily Inflation (USD): $${fmt.num(Math.round(raw.inflationUsd24h))}` : null,
  ].filter(Boolean).join('\n');

  sections['MEMPOOL'] = `
Pending Transactions: ${fmt.num(raw.mempoolSize)}
Mempool Size: ${fmt.bytes(raw.mempoolUsage)} / 300 MB
Mempool Usage: ${raw.mempoolUsage && raw.mempoolMaxSize ? `${((raw.mempoolUsage / raw.mempoolMaxSize) * 100).toFixed(1)}%` : 'N/A'}`;

  // Category to section mapping
  const CATEGORY_SECTIONS = {
    market: ['BITCOIN MARKET DATA'],
    network: ['NETWORK SECURITY', 'DIFFICULTY ADJUSTMENT'],
    mining: ['HALVING & MINING ECONOMICS', 'LATEST BLOCK', 'FEE MARKET'],
    lightning: ['LIGHTNING NETWORK'],
    activity: ['ON-CHAIN ACTIVITY', 'MEMPOOL']
  };

  // Filter sections by selected categories
  let includedSections = Object.keys(sections);

  if (selectedCategories && selectedCategories.length > 0 && selectedCategories.length < 5) {
    const allowedSections = new Set();
    selectedCategories.forEach(cat => {
      (CATEGORY_SECTIONS[cat] || []).forEach(s => allowedSections.add(s));
    });
    includedSections = Object.keys(sections).filter(s => allowedSections.has(s));
  }

  // Build output
  let output = '';
  includedSections.forEach(sectionName => {
    output += `\n=== ${sectionName} ===${sections[sectionName]}\n`;
  });

  return output.trim();
};

/**
 * Get pre-calculated data for AI prompts
 * AI should use these exact values instead of recalculating
 */
export const getPreCalculatedDataForAI = (metrics) => {
  const { raw, progress } = metrics;
  const lines = [];

  if (progress.halving) {
    lines.push(`HALVING PROGRESS: ${progress.halving.progress}% complete`);
    lines.push(`HALVING BLOCKS LEFT: ${progress.halving.blocksLeft.toLocaleString()} blocks`);
    lines.push(`HALVING EST DAYS: ~${progress.halving.estimatedDays} days remaining`);
    lines.push(`CURRENT EPOCH: ${progress.halving.currentEpoch}th epoch`);
  }

  if (progress.difficulty) {
    lines.push(`DIFFICULTY PROGRESS: ${progress.difficulty.progress}% through current period`);
    lines.push(`DIFFICULTY BLOCKS LEFT: ${progress.difficulty.blocksLeft?.toLocaleString() || 'N/A'} blocks`);
    if (progress.difficulty.estimatedDays != null) {
      lines.push(`DIFFICULTY EST DAYS: ~${progress.difficulty.estimatedDays} days remaining`);
    }
    if (progress.difficulty.estimatedChange) {
      lines.push(`DIFFICULTY EST CHANGE: ${progress.difficulty.estimatedChange}`);
    }
  }

  if (raw.circulatingSupply) {
    const pctMined = (raw.circulatingSupply / 21000000 * 100).toFixed(2);
    const remaining = Math.round(21000000 - raw.circulatingSupply);
    lines.push(`SUPPLY MINED: ${pctMined}% of 21,000,000 BTC`);
    lines.push(`REMAINING TO BE MINED: ~${remaining.toLocaleString()} BTC`);
  }
  if (raw.blockSubsidy) {
    lines.push(`BLOCK SUBSIDY: ${(raw.blockSubsidy / 1e8).toFixed(4)} BTC`);
  }
  if (raw.lastBlockTotalFees != null && raw.blockSubsidy) {
    const feeSubsidyRatio = ((raw.lastBlockTotalFees / raw.blockSubsidy) * 100).toFixed(2);
    lines.push(`FEE/SUBSIDY RATIO (last block): ${feeSubsidyRatio}%`);
    const r = parseFloat(feeSubsidyRatio);
    const meaning = r < 1 ? 'Negligible (normal, multi-decade transition)'
      : r < 5 ? 'Modest (subsidy dominates, not "soon" fee-reliant)'
      : r < 20 ? 'Moderate (meaningful but subsidy still dominates)'
      : r < 50 ? 'Significant (fees becoming important)'
      : 'Very high (fees rival subsidy)';
    lines.push(`FEE/SUBSIDY MEANING: ${meaning}`);
  }
  if (raw.assetAge) {
    lines.push(`NETWORK AGE: ${raw.assetAge.toLocaleString()} days since Genesis Block`);
  }

  return lines.length > 0 ? lines.join('\n') : null;
};

/**
 * Get metrics summary for mini dashboard
 * Compatible with existing getMetricsSummary API
 */
export const getMetricsSummary = (metrics) => {
  const { raw, progress } = metrics;

  return {
    price: {
      current: raw.price,
      change24h: raw.priceChange24h,
      change7d: raw.priceChange7d,
      change30d: raw.priceChange30d,
      athDistance: raw.athChangePercent
    },
    network: {
      hashrate: raw.hashrate,
      difficulty: raw.difficulty,
      blockHeight: raw.blockHeight,
      nodes: raw.nodes,
      avgBlockTime: raw.avgBlockTime ? raw.avgBlockTime / 60 : null
    },
    mining: {
      blocksToHalving: progress.halving?.blocksLeft,
      halvingProgress: progress.halving?.progress,
      halvingDaysLeft: progress.halving?.estimatedDays,
      currentEpoch: progress.halving?.currentEpoch,
      currentReward: raw.blockSubsidy,
      lastBlockPool: raw.lastBlockPool,
      lastBlockFees: raw.lastBlockTotalFees,
      feeSubsidyRatio: (raw.lastBlockTotalFees != null && raw.blockSubsidy)
        ? (raw.lastBlockTotalFees / raw.blockSubsidy) * 100 : null,
      feeRates: {
        fastestFee: raw.fastestFee,
        halfHourFee: raw.halfHourFee,
        hourFee: raw.hourFee,
        economyFee: raw.economyFee,
        minimumFee: raw.minimumFee
      }
    },
    lightning: {
      nodes: raw.lnNodes,
      channels: raw.lnChannels,
      capacity: raw.lnCapacity,
      nodeChange: raw.lnNodeChange,
      channelChange: raw.lnChannelChange
    },
    activity: {
      transactions24h: raw.transactions24h,
      activeAddresses: raw.activeAddresses24h,
      mempoolTxCount: raw.mempoolSize,
      mempoolUsage: raw.mempoolUsage,
      mempoolMaxSize: raw.mempoolMaxSize
    },
    progressBars: {
      halving: progress.halving,
      difficulty: progress.difficulty
    }
  };
};

export default calculateCanonicalMetrics;
